import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { BaseComponent } from '../../../shared/utils/base.component';
import { AddressComponent } from '../../../shared/components/address/address.component';
import { SectionsService } from '../../../services/sections.service';
import { Router } from '@angular/router';
import { ReferralDataService } from '../../../services/referral-data.service';
import { Subscription } from 'rxjs';
import { ContactDetailsData } from '../../../interfaces/referralSections.interface';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { performValidation } from '../../../shared/utils/validation.util';
import { CommonModule } from '@angular/common';
import {
  RADIO_YES_NO_OPTIONS,
  STATIC_DATA,
} from '../../../shared/constants/static-data.constants';
import { UserService } from '../../../services/user.service';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { AutoFocusFirstInputDirective } from '../../../shared/directives/auto-focus-first-input.directive';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'ppl-contact-details',
  standalone: true,
  imports: [
    AutoFocusFirstInputDirective,
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NzGridModule,
    NzCardModule,
    NzSwitchModule,
    NzToolTipModule,
    AddressComponent,
    NzDividerModule,
    NzRadioModule,
    NzCheckboxModule,
    CommonModule,
  ],
  templateUrl: './contact-details.component.html',
  styleUrl: './contact-details.component.css',
})
export class ContactDetailsComponent
  extends BaseComponent
  implements OnInit, OnDestroy {
  private navigationSubscription: Subscription | undefined;
  @Input() mode = 'edit';
  @Input() source?: string = '';
  @Input() isEmployerTaskManagerSelf = null;
  @Input() contactDetailsData: any = null;
  filledInfo: ContactDetailsData = {};
  contactForm!: FormGroup;
  communicationType: any[] = [];
  formHasBeenFilled = false;
  radioOptions = RADIO_YES_NO_OPTIONS;
  showPSD = true;
  isMailing = true;
  flow: string | null = '';
  permanentValidationErrors: any;
  mailingValidationErrors: any;
  serviceValidationErrors: any;
  formReadonly = false;
  isEmployerAssociation = false;

  constructor(
    private fb: FormBuilder,
    private sectionService: SectionsService,
    private router: Router,
    protected referralDataService: ReferralDataService,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    this.contactForm = this.fb.group({
      preferredMethod: [[]],
      isSMSConstentGiven: [null],
      email: [''],
      altPhone: [''],
      mobile: [''],
      residentGt18: [''],
      yearsInState: [''],
      permanentAddress: this.fb.group({
        street1: [''],
        street2: [''],
        countyOrRegion: [''],
        state: [''],
        zipCode: [''],
        city: [''],
        psd_code: [''],
        mailing: [true],
      }),
      mailingAddress: this.fb.group({
        countyOrRegion: [''],
        street2: [''],
        city: [''],
        state: [''],
        zipCode: [''],
        street1: [''],
        psd_code: [''],
      }),
      serviceAddress: this.fb.group({
        countyOrRegion: [''],
        street2: [''],
        city: [''],
        state: [''],
        zipCode: [''],
        street1: [''],
        psd_code: [''],
      }),
    });

    const program = this.userService.getSelectedProgram();
    this.flow = this.sectionService.getFlow()!;
    const associationFlows = this.sectionService.getAssociationFlows();
    this.formReadonly = this.referralDataService.getAssociationsEditable();

    if (this.formReadonly) {
      this.contactForm.disable();
    } else {
      this.contactForm.enable();
    }

    let steps: any;
    if (this.flow === 'providerEnrollment') {
      steps = this.sectionService.getProviderSectionData(
        program?.program_code,
        STATIC_DATA.CONTACT_DETAILS
      );
      this.secData = steps;
    } else {
      const providerType = this.referralDataService.getReferralFormData()?.providerType;
      steps = this.sectionService.getSectionData(
        program?.program_code,
        this.source,
        this.referralDataService.getProviderType(this.flow, providerType)
      );
      this.secData = steps[STATIC_DATA.CONTACT_DETAILS];
    }

    this.showPSD = program?.program_code !== 'NY';
    const validFlows = [
      'referral',
      'providers',
      'participants',
      'add_independentContractor',
      'add_vendor',
      'add_individualProvider',
    ];
    this.isMailing = this.flow !== null && validFlows.includes(this.flow);

    this.navigationSubscription = this.contactForm.valueChanges.subscribe(
      () => {
        const hasValue = this.hasAnyValueInForm(this.contactForm);
        // trim down email, alt phone and mobile number before saving in context, and emitevent as false to avoid multiple api call from comm method component when we update contact details from there as well
        const email = this.contactForm.get('email')?.value;
        const altPhone = this.contactForm.get('altPhone')?.value;
        const mobile = this.contactForm.get('mobile')?.value;
        if (email) {
          this.contactForm.get('email')?.setValue(email.trim(), { emitEvent: false });
        }
        if (altPhone) {
          this.contactForm.get('altPhone')?.setValue(altPhone.trim(), { emitEvent: false });
        }
        if (mobile) {
          this.contactForm.get('mobile')?.setValue(mobile.trim(), { emitEvent: false });
        }

        this.contactForm.markAsDirty();
        console.log(hasValue);
        this.formHasBeenFilled = hasValue;

        if (!associationFlows.includes(this.flow ?? '')) {
          this.referralDataService.setReferralFormData(
            'isContactFormUpdated',
            this.formHasBeenFilled
          );
        } else {
          this.referralDataService.setAssociationFormData(
            'isContactFormUpdated',
            this.formHasBeenFilled
          );
        }
        // this.referralDataService.setReferralFormData("isContactFormUpdated", this.formHasBeenFilled);

        console.log(this.contactForm.value);
        if (!associationFlows.includes(this.flow ?? '')) {
          if (this.contactForm.get('permanentAddress')?.value.mailing) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { mailing, ...mailingAddressValue } =
              this.contactForm.get('permanentAddress')?.value;
            this.contactForm.value.mailingAddress = mailingAddressValue;
            /* console.log(mailingAddressValue, 'mailing address');
          console.log(this.contactForm.value.mailingAddress); */
          }
          this.referralDataService.setReferralFormData(
            'contactDetailsData',
            this.contactForm.value
          );
        } else {
          this.referralDataService.setAssociationFormData(
            'contactDetailsData',
            this.contactForm.getRawValue()
          );
        }
        this.referralDataService.setChanged(
          !hasValue ? false : this.contactForm.dirty
        );
        if (!associationFlows.includes(this.flow ?? '')) {
          this.referralDataService.setReferralFormData(
            'isContactFormDirty',
            !hasValue ? false : this.contactForm.dirty
          );
        } else {
          this.referralDataService.setAssociationFormData(
            'isContactFormDirty',
            !hasValue ? false : this.contactForm.dirty
          );
        }
      }
    );

    this.communicationType = this.getLookup('Communication_Type');

    if (associationFlows.includes(this.flow ?? '')) {
      const filledData =
        this.referralDataService.getAssociationDetails('contactDetailsData')!;
      if (filledData) {
        this.contactForm.patchValue(filledData);
      }
    }
    if (
      ((this.flow == 'referral' &&
        this.userService.getLeadEntry() == 'referral') ||
        this.flow === 'participantEnrollment' ||
        this.flow === 'providerEnrollment') &&
      !this.referralDataService.getReferralFormData().contactDetailsData
    ) {
      const filledData = this.referralDataService.getReferralDetails(
        'contactDetailsData',
        this.flow
      )!;
      if (filledData) {
        this.contactForm.patchValue(filledData);
      }
    }

    if (!associationFlows.includes(this.flow ?? '')) {
      if (this.referralDataService.getReferralFormData().contactDetailsData) {
        this.filledInfo =
          this.referralDataService.getReferralFormData().contactDetailsData;
        this.contactForm.patchValue(this.filledInfo);
      }
    } else {
      if (
        this.referralDataService.getAssociationFormData().contactDetailsData
      ) {
        this.filledInfo =
          this.referralDataService.getAssociationFormData().contactDetailsData;
        this.contactForm.patchValue(this.filledInfo);
      }
    }

    if (associationFlows.includes(this.flow ?? '')) {
      this.referralDataService.associationSaveClicked.subscribe(() => {
        let mailingAddressErrors: any = {};
        let serviceAddressErrors: any = {};
        const providerType = this.referralDataService.getReferralFormData()?.providerType;

        const permanentAddressErrors = performValidation(
          this.contactForm.get('permanentAddress')?.getRawValue(),
          this.secData,
          'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
        );
        if (permanentAddressErrors) {
          delete permanentAddressErrors?.email;
          delete permanentAddressErrors?.mobile;
          delete permanentAddressErrors?.altPhone;
          delete permanentAddressErrors?.preferredMethod;
          this.permanentValidationErrors = permanentAddressErrors;
        }
        if (!this.contactForm.get('permanentAddress')?.getRawValue().mailing) {
          mailingAddressErrors = performValidation(
            this.contactForm.get('mailingAddress')?.getRawValue(),
            this.secData,
            'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
          );

          if (mailingAddressErrors) {
            delete mailingAddressErrors?.email;
            delete mailingAddressErrors?.mobile;
            delete mailingAddressErrors?.altPhone;
            delete mailingAddressErrors.preferredMethod;
            this.mailingValidationErrors = mailingAddressErrors;
          }
          if (this.flow === 'providers' && providerType === 'Vendor') {
            serviceAddressErrors = performValidation(
              this.contactForm.get('serviceAddress')?.getRawValue(),
              this.secData,
              'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
            );
            if (serviceAddressErrors) {
              delete serviceAddressErrors?.email;
              delete serviceAddressErrors?.mobile;
              delete serviceAddressErrors?.altPhone;
              delete serviceAddressErrors.preferredMethod;
              this.serviceValidationErrors = serviceAddressErrors;
            }
          }
        }

        const formErrors = performValidation(
          this.contactForm.value,
          this.secData,
          'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
        );
        delete formErrors.city;
        delete formErrors.countyOrRegion;
        delete formErrors.state;
        delete formErrors.street1;
        delete formErrors.zipCode;
        this.validatePreferredMethodInput(formErrors);
        this.validationErrors = {
          ...permanentAddressErrors,
          ...mailingAddressErrors,
          ...serviceAddressErrors,
          ...formErrors,
        };
        this.referralDataService.setError(this.validationErrors);
        this.referralDataService.setAssociationFormData(
          'isContactFormValid',
          Object.keys(this.validationErrors).length == 0
        );
      });
    }

    this.referralDataService.previousClicked.subscribe((val: any) => {
      if (val === STATIC_DATA.CONTACT_DETAILS) {
        this.contactForm.get('permanentAddress')?.enable();
        this.contactForm.get('mailingAddress')?.enable();
        this.contactForm.get('serviceAddress')?.enable();
      }
    });

    this.referralDataService.nextClicked.subscribe((val: any) => {
      if (val === STATIC_DATA.CONTACT_DETAILS) {
        this.contactForm.get('permanentAddress')?.enable();
        this.contactForm.get('mailingAddress')?.enable();
        this.contactForm.get('serviceAddress')?.enable();
        let mailingAddressErrors: any = {};
        let serviceAddressErrors: any = {};
        const providerType = this.referralDataService.getReferralFormData()?.providerType;

        const permanentAddressErrors = performValidation(
          this.contactForm.get('permanentAddress')?.getRawValue(),
          this.secData,
          'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
        );
        if (permanentAddressErrors) {
          delete permanentAddressErrors?.email;
          delete permanentAddressErrors?.mobile;
          delete permanentAddressErrors?.altPhone;
          delete permanentAddressErrors?.preferredMethod;
          this.permanentValidationErrors = permanentAddressErrors;
        }
        if (!this.contactForm.get('permanentAddress')?.getRawValue().mailing) {
          mailingAddressErrors = performValidation(
            this.contactForm.get('mailingAddress')?.getRawValue(),
            this.secData,
            'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
          );
          if (mailingAddressErrors) {
            delete mailingAddressErrors?.email;
            delete mailingAddressErrors?.mobile;
            delete mailingAddressErrors?.altPhone;
            delete mailingAddressErrors?.preferredMethod;
            this.mailingValidationErrors = mailingAddressErrors;
          }
          if (this.flow === 'providers' && providerType === 'Vendor') {
            serviceAddressErrors = performValidation(
              this.contactForm.get('serviceAddress')?.getRawValue(),
              this.secData,
              'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
            );
            if (serviceAddressErrors) {
              delete serviceAddressErrors?.email;
              delete serviceAddressErrors?.mobile;
              delete serviceAddressErrors?.altPhone;
              delete serviceAddressErrors.preferredMethod;
              this.serviceValidationErrors = serviceAddressErrors;
            }
          }
        }

        const formErrors = performValidation(
          this.contactForm.value,
          this.secData,
          'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType()
        );
        delete formErrors.city;
        delete formErrors.countyOrRegion;
        delete formErrors.state;
        delete formErrors.street1;
        delete formErrors.zipCode;
        this.validatePreferredMethodInput(formErrors);
        this.validationErrors = {
          ...permanentAddressErrors,
          ...mailingAddressErrors,
          ...serviceAddressErrors,
          ...formErrors,
        };
        this.applyCaliforniaOnlyStateError();
        this.referralDataService.setError(this.validationErrors);
      }
    });

    const enrollmentTypes = this.sectionService.getEnrollmentType();
    this.isEmployerAssociation = Array.isArray(enrollmentTypes) && enrollmentTypes.includes('employer');
    if (this.isEmployerAssociation) {
      const preferredMethods = this.contactForm.get('preferredMethod')?.value || [];
      if (!preferredMethods.includes('altPhone')) {
        preferredMethods.push('altPhone');
        this.contactForm.get('preferredMethod')?.setValue(preferredMethods);
      }
    }
  }

  // For the CASDP participant referral and participant enrollment flows, the
  // physical (and mailing) address state must be California. Otherwise, block
  // Next and show the error on the state field.
  private applyCaliforniaOnlyStateError(): void {
    // Feature flag: enabled unless explicitly turned off in the environment.
    if (environment.enableCaliforniaAddressValidation === false) {
      return;
    }
    const californiaOnlyFlows: string[] = [
      STATIC_DATA.REFERRAL,
      STATIC_DATA.PARTICIPANT_ENROLLMENT,
    ];
    const isCasdpCaliforniaFlow =
      californiaOnlyFlows.includes(this.flow ?? '') &&
      this.userService.getSelectedProgram()?.program_code === STATIC_DATA.CASDP_PROGRAM_CODE;
    if (!isCasdpCaliforniaFlow) {
      return;
    }
    this.flagNonCaliforniaState('permanentAddress', 'permanentValidationErrors');
    // The mailing address is only captured when it differs from the physical one.
    if (!this.contactForm.get('permanentAddress')?.getRawValue().mailing) {
      this.flagNonCaliforniaState('mailingAddress', 'mailingValidationErrors');
    }
  }

  // Flag the given address' state field when it isn't California, surfacing the
  // error both for display and for Next-button gating.
  private flagNonCaliforniaState(
    addressGroupName: string,
    errorsKey: 'permanentValidationErrors' | 'mailingValidationErrors',
  ): void {
    const state = (this.contactForm.get(addressGroupName)?.getRawValue()?.state ?? '')
      .toString()
      .trim()
      .toUpperCase();
    if (state && !STATIC_DATA.CALIFORNIA_STATE_VALUES.includes(state)) {
      this[errorsKey] = {
        ...(this[errorsKey] || {}),
        state: STATIC_DATA.CALIFORNIA_ONLY_MESSAGE,
      };
      this.validationErrors = {
        ...this.validationErrors,
        state: STATIC_DATA.CALIFORNIA_ONLY_MESSAGE,
      };
    }
  }

  validatePreferredMethodInput(formErrors: any): void {
    const preferredMethods = this.contactForm.get('preferredMethod')?.value;
    preferredMethods.forEach((method: string) => {
      if (method === 'email' && this.contactForm.get('email')?.value === '') {
        formErrors.email = 'Please enter the Email Address';
        this.validationErrors['email'] = 'Please enter the Email Address';
      }
      if (method === 'mobile' && this.contactForm.get('mobile')?.value === '') {
        formErrors.mobile = 'Please enter the Mobile Number';
        this.validationErrors['mobile'] = 'Please enter the Mobile Number';
      }
      if (method === 'altPhone' && this.contactForm.get('altPhone')?.value === '') {
        formErrors.altPhone = 'Please enter the Telephone Number';
        this.validationErrors['altPhone'] = 'Please enter the Telephone Number';
      }
    });
  }

  onCheckboxChange(isChecked: boolean, value: any): void {
    if (!isChecked && value === 'altPhone' && this.isEmployerAssociation) {
      return;
    }
    const selectedOptions = this.contactForm.get('preferredMethod')?.value || [];
    if (isChecked) {
      if (!selectedOptions.includes(value)) {
        selectedOptions.push(value);
        if (value === 'mobile' || value === 'altPhone') {
          this.contactForm.get('isSMSConstentGiven')?.setValue(true);
        }
      }
    } else {
      const index = selectedOptions.indexOf(value);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      }

      if (!selectedOptions.includes('mobile') && !selectedOptions.includes('altPhone')) {
        this.contactForm.get('isSMSConstentGiven')?.setValue(false);
      }
    }

    this.contactForm.get('preferredMethod')?.setValue(selectedOptions);
  }

  getFormGroup(controlName: string): FormGroup {
    return this.contactForm.get(controlName) as FormGroup;
  }

  limitDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 10) {
      value = value.slice(0, 10);

      event.target.value = value;
      this.contactForm.get('mobile')?.setValue(value);
    }
  }

  limitAltPhoneDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 10) {
      value = value.slice(0, 10);

      event.target.value = value;
      this.contactForm.get('altPhone')?.setValue(value);
    }
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }
}
